import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import ToastMessage from '../../components/ToastMessage';
import { SnackbarSeverityType } from '../../enums';
import {
  academicYears,
  defaultAcademicYear
} from '../../constants/academicYears';
import { useSchoolClassesConfigHook } from '../common/hooks/useSchoolClassesConfig';
import RolloverStatusFilters from './components/RolloverStatusFilters';
import RolloverStatusActions from './components/RolloverStatusActions';
import RolloverStatusTable from './components/RolloverStatusTable';
import report from '../../apis/report';
import { transformRolloverStatusData } from './utils/transformRolloverStatusData';
import { isEmpty } from 'lodash';
import { downloadReport } from '../../utils/reportUtils';
import moment from 'moment';
import { PER_PAGE, ROLLOVER_STATUS_REPORT_FILE_PREFIX } from './utils/utils';
import { getUniqueGrades } from '../../utils/gradeUtils';
import { PaperWrapper, Title } from '../common/styled';

const RolloverStatusPage: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.profile);
  const [selectedSchool, setSelectedSchool] = useState<string>('');

  const userSchools: string[] = Array.isArray(user?.schools)
    ? (user!.schools as string[])
    : [];
  const showSchoolDropdown = userSchools.length > 1;
  const effectiveSchool: string = showSchoolDropdown
    ? selectedSchool
    : userSchools.length === 1
      ? userSchools[0]
      : (user?.school as string) || '';

  const { schoolClassesConfig } = useSchoolClassesConfigHook(effectiveSchool);
  const [academicYear, setAcademicYear] = useState<string>(defaultAcademicYear);
  const [grade, setGrade] = useState<string>('All Grades');
  const [rows, setRows] = useState<any[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState<SnackbarSeverityType>(
    SnackbarSeverityType.success
  );

  const showToast = (message: string, severity: SnackbarSeverityType) => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };
  const handleCloseToast = () => setToastOpen(false);

  const academicYearOptions = academicYears.map((academicYear) => ({
    value: academicYear.value,
    label: academicYear.label
  }));
  const grades: string[] = Array.isArray(schoolClassesConfig?.grades)
    ? (schoolClassesConfig!.grades as string[])
    : [];
  const uniqueGrades = getUniqueGrades(grades);
  const rolloverGradeOptions: { value: string; label: string }[] = [
    { value: 'All Grades', label: 'All Grades' },
    ...uniqueGrades.map((grade) => ({ value: grade, label: grade }))
  ];

  const fetchAllPages = async (school: string) => {
    let pageNo = 1;
    const allData: any[] = [];
    let result: any[] = [];
    try {
      do {
        const response: any = await report.fetchRolloverStatusReport({
          academicYear,
          grade: grade === 'All Grades' ? '' : grade,
          perPage: PER_PAGE,
          pageNo,
          school: school
        });
        result = response || [];
        if (!isEmpty(result)) {
          allData.push(...result);
        }
        pageNo++;
      } while (!isEmpty(result));
    } catch (error) {
      throw error;
    }
    return allData;
  };

  const handleViewReport = async () => {
    setViewLoading(true);
    setRows([]);
    try { 
      const schoolForReport = effectiveSchool;
      const data = await fetchAllPages(schoolForReport);
      const rolloverStatusData = transformRolloverStatusData(data as any);
      setRows(rolloverStatusData);
      setHasSearched(true);
      showToast(
        'Successfully fetched rollover status report',
        SnackbarSeverityType.success
      );
    } catch (error: any) {
      showToast(
        error?.errors?.[0]?.message || 'Error fetching rollover status report',
        SnackbarSeverityType.error
      );
      setHasSearched(false);
    } finally {
      setViewLoading(false);
    }
  };

  const handleDownloadReport = async () => {
    setDownloadLoading(true);
    try {
      const schoolForReport = effectiveSchool;
      const data = await fetchAllPages(schoolForReport);
      const rolloverStatusData = transformRolloverStatusData(data as any, true);
      if (isEmpty(rolloverStatusData)) {
        showToast(
          'There is no data available to download as an Excel file.',
          SnackbarSeverityType.error
        );
        return;
      }
      const fileName = `${
        effectiveSchool
      }-${ROLLOVER_STATUS_REPORT_FILE_PREFIX}-${moment().format(
        'DD-MM-YYYY_HH-mm-ss'
      )}`;
      downloadReport(
        rolloverStatusData,
        fileName,
        'Rollover Status Report',
        showToast
      );
      showToast('Report downloaded successfully', SnackbarSeverityType.success);
    } catch (error: any) {
      showToast(
        error.errors?.[0]?.message ||
          'Error downloading rollover status report',
        SnackbarSeverityType.error
      );
    } finally {
      setDownloadLoading(false);
    }
  };

  return (
    <>
      <Title>Promotion Status Report</Title>
      <PaperWrapper>
        <RolloverStatusFilters
          academicYear={academicYear}
          setAcademicYear={setAcademicYear}
          grade={grade}
          setGrade={setGrade}
          gradeOptions={rolloverGradeOptions}
          academicYearOptions={academicYearOptions}
          setHasSearched={setHasSearched}
          selectedSchool={selectedSchool}
          setSelectedSchool={setSelectedSchool}
          userSchools={showSchoolDropdown ? userSchools : []}
        />
        <RolloverStatusActions
          onViewReport={handleViewReport}
          onDownloadReport={handleDownloadReport}
          viewLoading={viewLoading}
          downloadLoading={downloadLoading}
          isDisabled={showSchoolDropdown && !selectedSchool}
        />
      </PaperWrapper>
      {hasSearched && !viewLoading && (
        <RolloverStatusTable rows={rows} loading={viewLoading} />
      )}
      <ToastMessage
        open={toastOpen}
        message={toastMessage}
        severity={toastSeverity}
        onClose={handleCloseToast}
      />
    </>
  );
};

export default RolloverStatusPage;
